import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { Cascader, Select } from 'antd'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms/button'
import { DatePicker } from '@/components/organisms/customer/sales-manager/date-picker'
import { PaymentSettingType, SalesType } from '@/models/sales'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { ReservationType } from '@/models/reservation'
import { getRoomCascadeOptionsByReservations } from '@/utils/reservations'
import { initializeSelectBoxStyle } from '@/constants/form'
import '@/components/molecules/customer/sales-manager/usage-detail/ant-select.css'
import { SubjectSelect } from './subject-select'
import { unitPriceStyle } from './not-confirmed-usage-detail'
import { cascaderNameSearch } from '@/libs/cascader-name-search'
import { SalesSubjectMasterType } from '@/models/sales-manager/sales'

const QUANTITY_INPUT_PATTERN = /^-?\d*$/

type PostClosingConfirmedUsageDetailProps = {
  index: number
  usageDetail: SalesType
  advanceCompany?: any
  isEditing: boolean
  canPostClosingEdit: boolean
  onStartEdit: () => void
  onMarkDelete: () => void
  onCancelEdit: () => void
  onSaveEdit: () => void
  pendingDelete?: boolean
  salesSubjects: SalesSubjectMasterType[]
  salesPayments: PaymentSettingType[]
  confirmedDay: string
  changePostClosingDate: (date: moment.Moment | null, index: number) => void
  changePostClosingState: (e: { target: { value: string; name: string } }, index: number) => void
  changePostClosingQuantity: (index: number, quantity: number) => void
  accountsReceivableSettings: AccountsReceivableSettingType[]
  reservations?: ReservationType[]
  currentTabReservationId?: string
}

export const PostClosingConfirmedUsageDetail: React.FC<PostClosingConfirmedUsageDetailProps> = ({
  index,
  usageDetail,
  advanceCompany,
  isEditing,
  canPostClosingEdit,
  onStartEdit,
  onMarkDelete,
  onCancelEdit,
  onSaveEdit,
  pendingDelete = false,
  salesSubjects,
  salesPayments,
  confirmedDay,
  changePostClosingDate,
  changePostClosingState,
  changePostClosingQuantity,
  accountsReceivableSettings,
  reservations,
  currentTabReservationId,
}) => {
  const { t } = useTranslation()
  const quantity = usageDetail.quantity
  const [quantityDraft, setQuantityDraft] = useState(() => String(quantity))

  useEffect(() => {
    if (!/^-?\d+$/.test(quantityDraft)) return
    if (Number(quantityDraft) === quantity) return
    setQuantityDraft(String(quantity))
  }, [quantity])

  const commitQuantityDraft = () => {
    const num = Number(quantityDraft) || 0
    changePostClosingQuantity(index, num)
    setQuantityDraft(String(num))
  }

  const newAdvanceCompany = advanceCompany?.paymentCompanies?.map(item => ({
    value: item?.id,
    label: item?.name,
    disabled: !item.currentBalance,
  }))
  const salesPaymentOptions =
    salesPayments?.map(item => {
      if (Object.keys(advanceCompany?.paymentMethod || {}).length && item?.id === advanceCompany?.paymentMethod?.id) {
        return { value: item?.id, label: item?.name, children: newAdvanceCompany }
      }
      return { value: item?.id, label: item?.name }
    }) || []

  const reservation = reservations?.find(r => r.reservationId === currentTabReservationId)
  const roomsOptions = getRoomCascadeOptionsByReservations({ reservation, reservations })
  const displayRender = (label: string[]) => label[label.length - 1]

  useEffect(() => {
    if (!isEditing) return
    if (!usageDetail.reservationId && currentTabReservationId) {
      changePostClosingState({ target: { value: currentTabReservationId, name: 'reservationId' } }, index)
    }
    if (usageDetail.salesAccountsReceivableMasterId == null) {
      changePostClosingState({ target: { value: '', name: 'salesAccountsReceivableMasterId' } }, index)
    }
  }, [
    isEditing,
    currentTabReservationId,
    usageDetail.reservationId,
    usageDetail.salesAccountsReceivableMasterId,
    changePostClosingState,
    index,
  ])

  if (!isEditing) {
    return (
      <tr css={listItemReadWrapperStyle}>
        <td>{usageDetail.salesDate && moment(usageDetail.salesDate).format(t('MM-DD'))}</td>
        <td>
          {usageDetail.salesSubSubjectName
            ? `${usageDetail.salesSubjectName}/${usageDetail.salesSubSubjectName}`
            : usageDetail.salesSubjectName}
        </td>
        <td>{usageDetail?.roomNumber ? `${usageDetail.roomNumber}(${usageDetail.guestRoomAssignShortId})` : '-'}</td>
        <td>{Number(usageDetail.salesSubjectPrice).toLocaleString()}</td>
        <td>{Number(usageDetail.quantity).toLocaleString()}</td>
        <td>{Number(usageDetail.salesPrice).toLocaleString()}</td>
        <td>
          {usageDetail.companyId
            ? advanceCompany?.paymentCompanies?.find(item => item?.id === usageDetail.companyId)?.name
            : usageDetail.salesPaymentName}
        </td>
        {!usageDetail.isPaid ? <td css={unpaidStyle}>{t('Unpaid')}</td> : <td>{t('Paid')}</td>}
        <td className="accountsReceivable">{usageDetail.salesAccountsReceivableName || '-'}</td>
        <td css={actionCellStyle}>
          {canPostClosingEdit && (
            <button type="button" css={memoButtonStyle} onClick={onStartEdit} aria-label={t('Edit')}>
              <img alt="memo" src={require('@/static/images/memo.svg')} css={memoIconStyle} />
            </button>
          )}
        </td>
      </tr>
    )
  }

  const editActionsRow = (
    <tr css={editActionsRowStyle}>
      {pendingDelete ? (
        <td colSpan={10} css={editActionsCellStyle}>
          <div css={editActionsWrapperStyle}>
            <Button buttonType={3} width={80} height={32} fontSize={12} marginRight={16} onClick={onCancelEdit}>
              {t('Cancel')}
            </Button>
            <Button buttonType={1} width={80} height={32} fontSize={12} onClick={onSaveEdit}>
              {t('Save')}
            </Button>
          </div>
        </td>
      ) : (
        <>
          <td colSpan={8} css={editActionsSpacerStyle} />
          <td colSpan={2} css={editActionsCellStyle}>
            <div css={editActionsWrapperStyle}>
              <Button buttonType={3} width={80} height={32} fontSize={12} marginRight={16} onClick={onCancelEdit}>
                {t('Cancel')}
              </Button>
              <Button buttonType={1} width={80} height={32} fontSize={12} onClick={onSaveEdit}>
                {t('Save')}
              </Button>
            </div>
          </td>
        </>
      )}
    </tr>
  )

  if (pendingDelete) {
    return editActionsRow
  }

  return (
    <>
      <tr css={editRowStyle}>
        <td>
          <DatePicker
            date={usageDetail.salesDate ? moment(usageDetail.salesDate) : null}
            setDate={value => {
              const currentDate = usageDetail.salesDate ? moment(usageDetail.salesDate) : null
              const resolved = typeof value === 'function' ? value(currentDate) : value
              changePostClosingDate(resolved, index)
            }}
            confirmedDay={confirmedDay}
            allowClosedDates={true}
            inputId={`post-closing-usage-date-${index}`}
            initialVisibleMonth={() => moment(usageDetail.salesDate || undefined)}
          />
        </td>
        <td>
          <SubjectSelect
            onChange={value => {
              changePostClosingState({ target: { value: value.subjectMaster.id, name: 'salesSubjectId' } }, index)
              changePostClosingState({ target: { value: value.subSubject?.id ?? '', name: 'salesSubSubjectId' } }, index)
            }}
            defaultValue={usageDetail}
            salesSubjectMasters={salesSubjects}
            isEditMode={true}
          />
        </td>
        <td>
          <Select
            onChange={value => {
              const reservationId = roomsOptions?.find(room => room.value === value)?.reservationId
              changePostClosingState({ target: { value: reservationId ?? '', name: 'reservationId' } }, index)
              changePostClosingState({ target: { value: value as string, name: 'guestRoomAssignId' } }, index)
            }}
            value={usageDetail?.guestRoomAssignId || undefined}
            suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
            placeholder={t('Please select')}
          >
            {roomsOptions.map(room => (
              <Select.Option key={room.value} value={room.value}>
                {room.label}
              </Select.Option>
            ))}
          </Select>
        </td>
        <td>
          <input
            name="salesSubjectPrice"
            type="text"
            css={unitPriceStyle}
            value={usageDetail.salesSubjectPrice}
            onChange={e => changePostClosingState(e, index)}
            placeholder="0000"
            className="unitPriceStyle"
          />
        </td>
        <td>
          <input
            name="quantity"
            type="text"
            css={smallSelectBoxStyle}
            value={quantityDraft}
            onChange={e => {
              const value = e.target.value
              if (!QUANTITY_INPUT_PATTERN.test(value)) return
              setQuantityDraft(value)
              if (/^-?\d+$/.test(value)) {
                changePostClosingQuantity(index, Number(value))
              }
            }}
            onBlur={commitQuantityDraft}
            placeholder="0"
            className="quantityStyle"
          />
        </td>
        <td>{Number(usageDetail.salesPrice).toLocaleString()}</td>
        <td>
          <Cascader
            options={salesPaymentOptions}
            expandTrigger="hover"
            className="cascader-customize"
            value={usageDetail.companyId ? [usageDetail.salesPaymentId, usageDetail.companyId] : [usageDetail.salesPaymentId]}
            displayRender={displayRender}
            popupClassName="popup-cascader-customize"
            showSearch={cascaderNameSearch}
            onChange={async value => {
              await changePostClosingState({ target: { value: value[0] as string, name: 'salesPaymentId' } }, index)
              changePostClosingState({ target: { value: value[1] ? (value[1] as string) : '', name: 'companyId' } }, index)
            }}
            allowClear={false}
            suffixIcon={<span></span>}
          />
        </td>
        <td>
          <select
            name="isPaid"
            css={editModeMiddleSelectBoxStyle}
            className={!usageDetail.isPaid ? 'unpaidStyle' : 'paidStyle'}
            value={!usageDetail.isPaid ? '0' : '1'}
            onChange={e => changePostClosingState(e, index)}
          >
            <option value="0">{t('Unpaid')}</option>
            <option value="1">{t('Paid')}</option>
          </select>
        </td>
        <td>
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={true}
            suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} alt="" />}
            onChange={value => {
              changePostClosingState({ target: { value: value as string, name: 'salesAccountsReceivableMasterId' } }, index)
            }}
            value={usageDetail.salesAccountsReceivableMasterId}
          >
            <Select.Option value="">-</Select.Option>
            {accountsReceivableSettings.map((accountsReceivable, arIndex) => (
              <Select.Option key={arIndex} value={accountsReceivable.id}>
                {accountsReceivable.name}
              </Select.Option>
            ))}
            {usageDetail.salesAccountsReceivableMasterId !== '' &&
              !accountsReceivableSettings.some(ar => ar.id === usageDetail.salesAccountsReceivableMasterId) && (
                <Select.Option value={usageDetail.salesAccountsReceivableMasterId}>{usageDetail.salesAccountsReceivableName}</Select.Option>
              )}
          </Select>
        </td>
        <td css={actionCellStyle}>
          <button type="button" css={iconButtonStyle} onClick={onMarkDelete} aria-label={t('Delete')}>
            <img alt="close" src={require('@/static/images/cancel.svg')} css={closeIconStyle} />
          </button>
        </td>
      </tr>
      {editActionsRow}
    </>
  )
}

const unpaidStyle = css({
  color: '#F2A40B !important',
})

const listItemReadWrapperStyle = css({
  ':hover': {
    boxShadow: 'none',
    cursor: 'default',
  },
  'td.accountsReceivable, td .accountsReceivable': {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  td: {
    backgroundColor: '#F6F6F6',
    height: 57,
  },
})

const editRowStyle = css({
  td: {
    height: 57,
    verticalAlign: 'middle',
    borderBottom: 'none',
  },
})

const editActionsRowStyle = css({
  td: {
    borderTop: 'none',
    paddingTop: 0,
    paddingBottom: 12,
    verticalAlign: 'top',
  },
})

const editActionsSpacerStyle = css({
  padding: 0,
})

const editActionsCellStyle = css({
  textAlign: 'right',
  paddingRight: 8,
})

const editActionsWrapperStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
})

const actionCellStyle = css({
  textAlign: 'right',
  verticalAlign: 'middle',
  paddingRight: 8,
})

const iconButtonStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
})

const memoButtonStyle = css(iconButtonStyle)

const memoIconStyle = css({
  width: 16,
  height: 16,
})

const closeIconStyle = css({
  width: 16,
  height: 16,
})

const selectBoxStyle = css(initializeSelectBoxStyle, {
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 10,
  display: 'block',
  paddingLeft: 14,
  paddingRight: 14,
  marginRight: 10,
})

const smallSelectBoxStyle = css(selectBoxStyle, {
  width: '100%',
  backgroundPosition: '80% center',
})

const middleSelectBoxStyle = css(selectBoxStyle, {
  backgroundPosition: 'calc(100% - 8px) center',
})

const editModoSelectBoxStyle = css(selectBoxStyle, {
  backgroundImage: `url(${require('@/static/images/select.svg')})`,
})

const editModeMiddleSelectBoxStyle = css(middleSelectBoxStyle, editModoSelectBoxStyle)
